/**
Note: This implementation was built by referencing Editor AI
packages/editor/editor-plugin-ai/src/prebuilt/content-transformers/markdown-to-pm/md/media-md-plugin.ts

Aside from a refactor, the key differences in logic are:
- Our regex is more specific, and will only match images that are not inside single backticks otherwise we'll see AI Mate render images surrounded by single backticks
*/
export type Token = {
	new (type: string, tag: string, level: number): Token;
	type: string;
	content: string;
	level: number;
	tag: string;
	attrs?: string[][];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	children?: any[];
};

export interface MdState {
	Token: Token;
	tokens: Token[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	md: any;
}

function createRule() {
	// Match any images that aren't inside single backticks
	const mdImageRegex = /(?<!`)!\[[^\]]*\]\([^\)]+\)(?!`)/g;
	const validParentTokens = ['th_open', 'td_open', 'list_item_open'];

	return function media(State: MdState) {
		const getUrl = (str: string) => {
			const res = State.md.helpers.parseLinkDestination(str, str.indexOf('(') + 1, str.length);
			if (res.ok) {
				const href = State.md.normalizeLink(res.str);
				if (State.md.validateLink(href)) {
					return href;
				}
			}

			return '';
		};

		const createMediaTokens = (url: string) => {
			const mediaSingleOpen = new State.Token('media_single_open', '', 1);
			const media = new State.Token('media', '', 0);
			media.attrs = [
				['url', getUrl(url)],
				['type', 'external'],
			];
			const mediaSingleClose = new State.Token('media_single_close', '', -1);

			return [mediaSingleOpen, media, mediaSingleClose];
		};

		const createInlineTokens = (str: string, openingTokens: Token[], closingTokens: Token[]) => {
			if (!str || str.length === 0) {
				return [];
			}

			const inlineBefore = new State.Token('inline', '', 1);
			inlineBefore.content = str;
			inlineBefore.children = [];

			return [...openingTokens, inlineBefore, ...closingTokens];
		};

		let processedTokens: string[] = [];
		const newTokens = State.tokens.reduce(
			(tokens: Token[], token: Token, i: number, arr: Token[]) => {
				if (token.type === 'inline' && mdImageRegex.test(token.content)) {
					const openingTokens: Token[] = [];
					let cursor = i - 1;
					let previousToken = arr[cursor];
					let subTree: Token[] = [];

					// Loop backwards through the tokens until a valid parent token is found
					while (
						previousToken &&
						previousToken.level > 0 &&
						validParentTokens.indexOf(previousToken.type) === -1
					) {
						openingTokens.unshift(previousToken);
						cursor--;
						previousToken = arr[cursor];
					}

					// If the previous token is not a valid parent token, add it to the start of the opening tokens array
					if (validParentTokens.indexOf(previousToken.type) === -1) {
						openingTokens.unshift(previousToken);
					} else {
						cursor++;
					}

					// Create closing tokens for each opening token and reverse the array
					const closingTokens = openingTokens
						.map((token) => new State.Token(token.type.replace('_open', '_close'), token.tag, -1))
						.reverse();

					const matches = token.content.match(mdImageRegex)!;

					// Process all images in the inline token
					let inlineContentStack = token.content;
					matches.forEach((match) => {
						const start = inlineContentStack.indexOf(match);
						const contentBefore = inlineContentStack.substring(0, start);
						inlineContentStack = inlineContentStack.substring(start + match.length);

						// Add the inline tokens and media tokens to the subtree
						subTree = [
							...subTree,
							...createInlineTokens(contentBefore, openingTokens, closingTokens),
							...createMediaTokens(match),
						];
					});

					// If there is any remaining content in the stack, add the inline tokens to the subtree
					if (inlineContentStack.length) {
						subTree = [
							...subTree,
							...createInlineTokens(inlineContentStack, openingTokens, closingTokens),
						];
					}

					processedTokens = [...processedTokens, ...closingTokens.map((c) => c.type)];

					tokens = [...tokens.slice(0, cursor), ...subTree];
				} else if (processedTokens.indexOf(token.type) !== -1) {
					// Ignore token if it's already processed
					processedTokens.splice(processedTokens.indexOf(token.type), 1);
				} else {
					tokens.push(token);
				}

				return tokens;
			},
			[],
		);

		State.tokens = newTokens;
		return true;
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default (md: any): void => {
	md.core.ruler.before('inline', 'media', createRule());
};
